import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';
import { TwoFactorService } from '../../../core/services/two-factor.service';
import { safeInternalReturnUrl } from '../../../core/utils/return-url.util';
import { take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

@Component({
    selector: 'app-backoffice-login',
    templateUrl: './backoffice-login.component.html',
    styleUrls: ['./backoffice-login.component.css'],
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterModule]
})
export class BackofficeLoginComponent {

  form: FormGroup;
  loading = false;
  errorMessage = '';

  /**
   * #1315 — destination d'origine. Cet écran ne fait pas que l'utiliser à la
   * fin : il la **transmet** aux écrans 2FA, qui sont l'étape suivante du même
   * parcours. Sans ce relais, la destination survivrait au login pour être
   * jetée une écran plus loin.
   */
  private readonly returnUrl: string;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private twoFactorService: TwoFactorService,
    private router: Router,
    route: ActivatedRoute,
  ) {
    this.returnUrl = safeInternalReturnUrl(
      route.snapshot.queryParams['returnUrl'],
      '/backoffice',
    );
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });
  }

  async onLogin(): Promise<void> {
    if (this.form.invalid) return;

    this.loading = true;
    this.errorMessage = '';

    const { email, password } = this.form.value;

    try {
      // Login direct Firebase Auth (pas besoin de linking pour les employés)
      const result = await this.authService.login(email, password);
      console.log('[BackofficeLogin] Login réussi:', result.user?.email);

      // Check 2FA status from employee profile
      const uid = result.user?.uid;
      if (uid) {
        const employee = await firstValueFrom(
          this.authService.getEmployeeProfileByUid(uid).pipe(take(1))
        );

        if (employee?.twoFactorEnabled) {
          // 2FA already configured → verify
          this.twoFactorService.setPendingUid(uid);
          await this.router.navigate(['/verify-2fa'], {
            queryParams: { returnUrl: this.returnUrl },
          });
          return;
        } else {
          // 2FA not yet configured → mandatory setup
          this.twoFactorService.setPendingUid(uid);
          await this.router.navigate(['/setup-2fa'], {
            queryParams: { returnUrl: this.returnUrl },
          });
          return;
        }
      }

      await this.router.navigateByUrl(this.returnUrl);
    } catch (err: any) {
      console.error('[BackofficeLogin] Error:', err);
      const code = err?.code || '';
      switch (code) {
        case 'auth/user-not-found':
        case 'auth/invalid-credential':
          this.errorMessage = 'Email ou mot de passe incorrect';
          break;
        case 'auth/wrong-password':
          this.errorMessage = 'Mot de passe incorrect';
          break;
        case 'auth/too-many-requests':
          this.errorMessage = 'Trop de tentatives. Veuillez réessayer plus tard.';
          break;
        case 'auth/user-disabled':
          this.errorMessage = 'Ce compte a été désactivé. Contactez votre administrateur.';
          break;
        default:
          this.errorMessage = 'Erreur de connexion. Veuillez réessayer.';
      }
    } finally {
      this.loading = false;
    }
  }
}
